import {
  actionsKeys,
  formatAmount,
  permissionActions,
} from "@/lib/utils";
import {
  setCurrentFinanceYear,
  setInvoiceDueDate,
} from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetClearingStat } from "@/services";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";

const useClearing = () => {
  const [openTabs, setOpenTabs] = useState<string[]>(["clearing"]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accessPermission, communityData, currentFinanceYear } = useSelector(
    (state: RootState) => state.currentData
  );

  const [clearingCards, setClearingCards] = useState([
    {
      id: "create",
      title: "Create Clearing Document",
      link: `${routes.add}`,
      module: actionsKeys?.MANUAL_CLEARING?.toLowerCase(),
      accessKeys: permissionActions.CREATE,
    },
    {
      id: "summary",
      data: [
        { label: "Total Cleared Amount", value: 0, apiKey: "clearedAmount" },
        { label: "Pending Clearance", value: 0, apiKey: "pendingAmount" },
        { label: "Total Variance", value: 0, apiKey: "varianceAmount" },
      ],
      title: "Customer Clearing Document",
      link: `${routes.listing}`,
      subtitle: "",
      subtitleColor: "text-yellow-600",
      module: actionsKeys?.MANUAL_CLEARING?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
  ]);

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: "",
        name: <span className="text-primary">{"Clearing"}</span>,
      },
    ],
    []
  );

  const onYearChange = (e: any) => {
    dispatch(setCurrentFinanceYear(e));
  };

  const onAccordionChange = (e: any) => {
    setOpenTabs(e);
  };

  const onExpandCollapse = () => {
    if (openTabs?.length) {
      setOpenTabs([]);
    } else {
      setOpenTabs(["clearing"]);
    }
  };

  const onCreateCardClick = (link: string) => () => {
    if (communityData) {
      navigate(link);
    } else {
      toast.error("Please select community");
    }
  };

  const onCardClick = (link: string) => () => {
    dispatch(setInvoiceDueDate(null));
    navigate(link);
  };

  const onBackClick = () => {
    navigate(`/${routes.bom}/${routes.finance}`);
  };

  const { data: clearingStat, isLoading: isClearingStatLoading } =
    useGetClearingStat({
      communityId: communityData?.communityId,
      year: currentFinanceYear,
      queryParams: {
        enabled: !!(communityData?.communityId && accessPermission[actionsKeys?.MANUAL_CLEARING?.toLowerCase()][permissionActions.READ]),
      },
    });

  useEffect(() => {
    if (!clearingStat) return;

    const statData = (clearingStat as any)?.data;

    const updatedCards = clearingCards.map((card: any) => {
      const { id, data: cardData, apiKey } = card;

      if (id === "summary") {
        const total = Object.values(statData || {})?.reduce(
          (acc: number, val: any) => {
            const num = parseFloat(val);
            return acc + (isNaN(num) ? 0 : num);
          },
          0
        );

        const updatedData = cardData.map((item: any) => ({
          ...item,
          value:
            item.apiKey === "total"
              ? total
              : formatAmount(statData?.[item.apiKey] ?? 0),
        }));

        return {
          ...card,
          data: updatedData,
          access: !!accessPermission?.[card.module]?.[card.accessKeys],
        };
      }

      return {
        ...card,
        count: formatAmount(statData?.[apiKey] ?? 0),
        access: !!accessPermission?.[card.module]?.[card.accessKeys],
      };
    });

    setClearingCards(updatedCards);
  }, [clearingStat, communityData?.communityId]);

  return {
    communityData,
    clearingCards,
    onAccordionChange,
    openTabs,
    onExpandCollapse,
    onCreateCardClick,
    onCardClick,
    breadcrumbData,
    onBackClick,
    isClearingStatLoading,
    onYearChange,
    currentFinanceYear,
  };
};

export default useClearing;
